/**
 * Silence Firebase SDK Logging
 * Patches Firebase SDK at the bundle level to prevent error logging
 * This must be done BEFORE Firebase SDK initializes
 */

export function silenceFirebaseSDK() {
  if (typeof window === 'undefined') return;

  const globalObj = window as any;

  // Attempt 1: Set Firebase log level to error (highest level, least logging)
  globalObj.__FIREBASE_LOG_LEVEL__ = 'error';

  // Attempt 2: Patch the default logger handler if available
  try {
    // Firebase's internal logging can be controlled via the logger
    // We'll override the console methods BEFORE Firebase uses them
    const originalWarn = console.warn;
    const originalLog = console.log;
    const originalError = console.error;

    // Track if we're currently inside Firebase logging to prevent recursion
    let isInsideFirebaseLog = false;

    // This pattern matches Firebase SDK's internal logger
    const firebaseLoggingPatterns = [
      'defaultLogHandler',
      'WebChannelConnection',
      '@firebase/',
      'Firestore',
      'transport errored',
      'Could not reach Cloud Firestore',
      '[code=unavailable]',
    ];

    const isFirebaseLog = (args: any[]): boolean => {
      const str = args.map(a => String(a)).join(' ');
      return firebaseLoggingPatterns.some(pattern => str.includes(pattern));
    };

    // Override warn (most Firebase errors come as warnings)
    console.warn = function(...args: any[]) {
      if (isFirebaseLog(args)) {
        return; // Silently ignore
      }
      originalWarn.apply(console, args);
    };

    // Override log
    console.log = function(...args: any[]) {
      if (isFirebaseLog(args)) {
        return; // Silently ignore
      }
      originalLog.apply(console, args);
    };

    // Override error (critical for catching Firebase errors)
    console.error = function(...args: any[]) {
      if (isFirebaseLog(args)) {
        return; // Silently ignore
      }
      originalError.apply(console, args);
    };
  } catch (e) {
    // Ignore patching errors
  }

  // Attempt 3: Patch the Firebase module's logger if we can access it
  // This is more nuclear - we try to find and disable Firebase's internal logging
  setTimeout(() => {
    try {
      // Look for the Firebase bundle's logger configuration
      // Firebase stores logger state in globals
      if ((globalObj as any).__FIREBASE_INSTANCE__) {
        try {
          const instance = (globalObj as any).__FIREBASE_INSTANCE__;
          // Try to disable logging on the instance
          if (instance.logger) {
            instance.logger = { 
              log: () => {},
              warn: () => {},
              error: () => {}
            };
          }
        } catch (e) {
          // Instance modification failed
        }
      }

      // Attempt to find and patch Firebase's logger in the global namespace
      // Firebase may attach its logger to various globals
      for (const key in globalObj) {
        try {
          const value = globalObj[key];
          if (
            value && 
            typeof value === 'object' && 
            (key.toLowerCase().includes('firebase') || key.toLowerCase().includes('logger'))
          ) {
            if (value.log) value.log = () => {};
            if (value.warn) value.warn = () => {};
            if (value.error) value.error = () => {};
          }
        } catch (e) {
          // Ignore errors during global search
        }
      }
    } catch (e) {
      // Ignore errors
    }
  }, 50);
}

export default silenceFirebaseSDK;
